import { computed } from 'vue';
import { useI18n } from '@/composables/useI18n';
import { useUrl } from '@/composables/useUrl';
import { reportTypeFromRoute } from '@/services/utils/reportType';

export const useColumnsSettingsDialog = () => {
  const { t } = useI18n();
  const urlApi = useUrl();

  const isSpecialDimensions = (dimension: string = reportTypeFromRoute()) => {
    return ['errors', 'postbacks', 'conversions', 'live-clicks', 'live-visits'].includes(dimension);
  };

  const isMetricAllowToInherit = (inheritColumns: boolean, dimension: string, type: string) => {
    if (isSpecialDimensions(dimension)) {
      if (dimension === type) {
        return true;
      }

      if (dimension === 'live-clicks') {
        return false;
      }

      if (dimension === 'live-visits') {
        return false;
      }

      return false;
    }

    return !isSpecialDimensions(type)
      && !isSpecialDimensions(dimension)
      && (inheritColumns || dimension === type);
  };

  const allowToSaveThisMetricOnServer = computed(() => {
    const type = reportTypeFromRoute();
    const list = [
      'ips', 'subnet-small', 'subnet-big', 'useragents', 'referrers', 'referrer-domains',
      'month', 'day', 'day-of-week', 'hour-of-day', 'live-clicks', 'live-visits',
      'custom1', 'custom2', 'custom3', 'custom4', 'custom5', 'custom6', 'custom7', 'custom8', 'custom9', 'custom10',
    ];
    return list.includes(type) && urlApi.isReport.value || !urlApi.isReport.value;
  });

  const groupingTranslation = (group: { id: string; name?: string }) => {
    switch (group.id) {
      case 'general':
        return t('____x.general');
      case 'integration_details':
        return t('Integration Details');
      case 'total_events_data':
        return t('Total Events Data');
      case 'basic_financial_data':
        return t('Basic Financial Data');
      case 'key_performance_data':
        return t('Key Performance Data');
      case 'advanced_data_on_events':
        return t('Advanced Data on Events');
      case 'ivt':
        return t('Invalid Traffic Details');
      case 'advanced_conversions_data':
        return t('Advanced Conversions Data');
      case 'custom_columns':
        return t('Custom Columns');
      case 'postback_related_data':
        return t('Postback-related Data');
      case 'elements_related_data':
        return t('Elements-related Data');
      case 'categories_related_data':
        return t('Categories-related Data');
      case 'traffic_source_related_data':
        return t('Traffic Source-related Data');
      default:
        return group.name || group.id;
    }
  };

  const groupingTooltipTranslation = (group: { id: string }) => {
    switch (group.id) {
      case 'general':
        return t('General data about the element or category of report');
      case 'integration_details':
        return t('Data on the campaign integrated via API');
      case 'total_events_data':
        return t('Sum of data on each event type for the selected report period');
      case 'basic_financial_data':
        return t('Sum of cost, revenue and profit for the selected report period');
      case 'key_performance_data':
        return t('Metrics indicating the performance of the campaign, campaign element, or category for the selected report period');
      case 'advanced_data_on_events':
        return t('More detailed data on each type of tracked event for the selected report period');
      case 'ivt':
        return t('Data on invalid and suspicious traffic for the selected report period');
      case 'advanced_conversions_data':
        return t('More detailed data on the status and revenue of tracked conversions for the selected report period');
      case 'custom_columns':
        return t('Custom conversions and columns based on the user-defined formulas');
      case 'postback_related_data':
        return t('Data on postback of affiliate network or traffic source depending on the selected report');
      case 'elements_related_data':
        return t('Data on elements related to conversion or visit/click depending on the selected report');
      case 'categories_related_data':
        return t('Additional data on conversion or visit/click depending on the selected report');
      case 'traffic_source_related_data':
        return t('Data received from a traffic source which is associated with a conversion or visit/click depending on the report selected');
      default:
        return null;
    }
  };

  return {
    isSpecialDimensions,
    isMetricAllowToInherit,
    allowToSaveThisMetricOnServer,
    groupingTranslation,
    groupingTooltipTranslation,
  };
};
